import { ComputedRef, computed, reactive } from 'vue';
import { createRouter, createWebHistory, RouteRecordRaw, RouteLocation, Router } from 'vue-router';
import HomeRoute from '@/modules/home-module/routes';
import LoginRedirectRoutes from '@/modules/login-redirect-module/routes';
import { IRouteMeta } from './interfaces';
import RootRoutes from '@/modules/routes';
import CatalogusRoutes from '@/modules/catalogus-module/routes';
import ProductDetailRoutes from '@/modules/product-detail-module/routes';
import DownloadRoutes from '@/modules/downloads-module/routes';
import UserRoutes from '@/modules/user-module/routes';
import oldRoutes from './old-routes';
import ProductConfigRoutes from '@/modules/product-detail-module/modules/product-config-module/routes';
import { MetaDataHandler } from './guards/meta-data-guard';
import { AuthHandler } from './guards/auth-guard';
import { LatestLocationHandler } from './guards/latest-location-guard';
import { GtagHandler } from './guards/gtag-guard';

export interface IRouteData {
  name: RouteLocation['name'];
  params: RouteLocation['params'];
  query: RouteLocation['query'];
  meta: IRouteMeta | null;
}

export type RouterInstanceType = {
  router: Router | null;
  data: ComputedRef<IRouteData>;
};

const routeData: IRouteData = reactive({
  params: {},
  query: {},
  name: null,
  meta: null
});

const routes: RouteRecordRaw[] = [
  ...ProductConfigRoutes,
  ...ProductDetailRoutes,
  ...CatalogusRoutes,
  ...RootRoutes,
  ...HomeRoute,
  ...DownloadRoutes,
  ...UserRoutes,
  ...LoginRedirectRoutes,
  ...oldRoutes
];

const router = createRouter({
  history: createWebHistory(),
  routes: routes
});

router.beforeEach(MetaDataHandler);
router.beforeEach(AuthHandler);
router.beforeEach(LatestLocationHandler);
router.beforeEach(GtagHandler);

router.afterEach((route) => {
  const isSameRoute =
    route.name === routeData.name && JSON.stringify(route.params ?? {}) === JSON.stringify(routeData.params ?? {});

  if (!isSameRoute) {
    routeData.meta = route.meta || null;
  }

  routeData.params = route.params;
  routeData.query = route.query;
  routeData.name = route.name;
});

export const RouterInstance: RouterInstanceType = {
  router: router,
  data: computed(() => routeData)
};

export const updateRouteMeta = ({
  title,
  description,
  keywords,
  breadcrumbValue,
  breadcrumbPath
}: {
  title?: string;
  description?: string;
  keywords?: string[];
  breadcrumbValue?: string;
  breadcrumbPath?: string;
}) => {
  if (RouterInstance.router) {
    if (!RouterInstance.router.currentRoute.value.meta) {
      RouterInstance.router.currentRoute.value.meta = {};
    }
    if (RouterInstance.router.currentRoute.value.meta) {
      if (breadcrumbValue) {
        const currentBreadcrumb = RouterInstance.router.currentRoute.value.meta.breadcrumb;
        const existingBreadcrumb =
          typeof currentBreadcrumb === 'object' && currentBreadcrumb !== null ? currentBreadcrumb : {};

        const newBreadcrumb = {
          ...existingBreadcrumb,
          dynamicValue: breadcrumbValue,
          ...(breadcrumbPath && { dynamicPath: breadcrumbPath })
        };

        RouterInstance.router.currentRoute.value.meta.breadcrumb = newBreadcrumb;

        routeData.meta = {
          ...routeData.meta,
          breadcrumb: newBreadcrumb
        };
      }
      RouterInstance.router.currentRoute.value.meta.title = title;
      RouterInstance.router.currentRoute.value.meta.description = description;
      RouterInstance.router.currentRoute.value.meta.keywords = keywords;

      routeData.meta = {
        ...routeData.meta,
        title,
        description,
        keywords
      };
    }
  }
};

export default router;
